'use client'

import { ImportDataModal } from '@/components/import/ImportDataModal'
import DateRangeFilter from '@/components/date-range-filter'
import ListSortSelect, { AppointmentSort, AppointmentSortOrder } from '@/components/list-sort-select'
import { ClientSelector } from '@/components/client/ClientSelector'
import { PartnerSelector } from '@/components/partner/PartnerSelector'
import { UserSelector } from '@/components/UserSelector'
import {
    getOrdersByCompany,
    getOrderStatusColor,
    getOrderStatusLabel,
    Order,
    PROCEDURE_STATUS_FILTER_OPTIONS,
    PROCEDURE_STATUS_LABELS,
} from '@/lib/api/orders/order-api'
import { ShareableModule } from '@/lib/api/share/share-api'
import { useClientLabel } from '@/lib/hooks/use-client-label'
import { useDebounce } from '@/lib/hooks/use-debounce'
import { useServiceLabel } from '@/lib/hooks/use-service-label'
import { usePaginatedPage } from '@/lib/hooks/use-paginated-page'
import { useUrlPage } from '@/lib/hooks/use-url-page'
import PaginationControls from '@/components/pagination/PaginationControls'
import {
    FilterField,
    ListEmptyState,
    ListFilterBar,
    ListPageHeader,
} from '@/components/list-page'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useAuth } from '@/store/hooks'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useCallback, useMemo, useState } from 'react'
import {
    MdAccessTime,
    MdAdd,
    MdArrowForward,
    MdBusiness,
    MdFileDownload,
    MdHealthAndSafety,
    MdPerson,
    MdPersonPin,
    MdPointOfSale,
} from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { BillingStatusFilter } from '@/components/list-page/billing-status-filter'
import { resolveAppointmentBillingState } from '@/lib/billing/appointment-billing-state'

export default function OrdersPage() {
    const { user, isPartner, isEmployee } = useAuth()
    const hidePartnerSelector = isPartner || (isEmployee && !!user?.partnerId)
    const navigate = useNavigate()
    const { canCreate } = useResourcePermissions('bookings', 'orders')
    // Fora da saúde a tela é de "Serviços", e quem os recebe é cliente/aluno/tutor.
    const { clientLabel, ClientLabel } = useClientLabel()
    // `Order` (tipo) já está no escopo — os rótulos entram com sufixo.
    const {
        order: orderLabel,
        Order: OrderLabel,
        orders: ordersLabel,
        Orders: OrdersLabel,
    } = useServiceLabel()
    const [searchInput, setSearchInput] = useState('')
    const [filterById, setFilterById] = useState('')
    const [clientFilter, setClientFilter] = useState<number | null>(null)
    const [showImportModal, setShowImportModal] = useState(false)
    const [partnerFilter, setPartnerFilter] = useState<number | null>(null)
    /** Vendedor do atendimento — é o `createdById` (o mesmo que a transferência de vendedor move). */
    const [sellerFilter, setSellerFilter] = useState<number | null>(null)
    /**
     * Vazio = todos. Inclui "Cancelado" (procedimento cancelado não é excluído) e
     * "Aprovado", que não é status do banco: é `SCHEDULED` sem data marcada, o
     * mesmo recorte que o card mostra no badge. Ver
     * `PROCEDURE_STATUS_FILTER_OPTIONS`.
     */
    const [statusFilter, setStatusFilter] = useState<string>('')
    /** Status da cobrança vinculada. Vazio = todos; `NONE` = sem cobrança. */
    const [billingStatusFilter, setBillingStatusFilter] = useState('')
    const [dateRange, setDateRange] = useState<{
        startDate?: string
        endDate?: string
    }>({})
    // Criação como padrão: o operador abre a lista para achar o que acabou de
    // registrar, não o que está marcado para a semana que vem.
    const [sortBy, setSortBy] = useState<AppointmentSort>('created')
    // Direção separada do critério: o operador escolhe entre começar pelo mais
    // recente (padrão) ou pelo mais antigo.
    const [sortOrder, setSortOrder] = useState<AppointmentSortOrder>('desc')

    const debouncedSearch = useDebounce(searchInput, 300)
    const debouncedId = useDebounce(filterById, 300)

    const filters = useMemo(
        () => ({
            search: debouncedSearch || undefined,
            status: statusFilter || undefined,
            partnerId: partnerFilter || undefined,
            id: debouncedId ? Number(debouncedId) : undefined,
            clientId: clientFilter || undefined,
            createdById: sellerFilter || undefined,
            billingStatus: billingStatusFilter || undefined,
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
            sortBy,
            sortOrder,
            // Procedimentos nascidos de pacote são listados em /medical/packages.
            packages: 'exclude' as const,
        }),
        [
            debouncedSearch,
            statusFilter,
            partnerFilter,
            debouncedId,
            clientFilter,
            sellerFilter,
            billingStatusFilter,
            dateRange,
            sortBy,
            sortOrder,
        ]
    )

    const fetchFn = useCallback(
        (params: any) => {
            return getOrdersByCompany(user!.companyId!, params)
        },
        [user?.companyId]
    )

    const [urlPage, setUrlPage] = useUrlPage()

    const { items, loading, page, limit, total, setPage, reload } =
        usePaginatedPage<Order, typeof filters>({
            fetchFn,
            filters,
            limit: 15,
            enabled: !!user?.companyId,
            page: urlPage,
            onPageChange: setUrlPage,
        })

    const activeFilters = [
        searchInput,
        filterById,
        clientFilter,
        partnerFilter,
        sellerFilter,
        statusFilter,
        billingStatusFilter,
        dateRange.startDate,
    ].filter(Boolean).length

    const clearFilters = () => {
        setSearchInput('')
        setFilterById('')
        setClientFilter(null)
        setPartnerFilter(null)
        setSellerFilter(null)
        setStatusFilter('')
        setBillingStatusFilter('')
        setDateRange({})
    }

    const currency = (cents: number) =>
        (cents / 100).toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL',
        })

    /**
     * O que já entrou, em uma linha.
     *
     * O valor PAGO é o número que importa e leva a cor — o mesmo verde do "Pago",
     * que é como dinheiro que entrou aparece no resto do sistema. O que falta vem
     * junto, em tom neutro, porque é contexto e não o dado principal. Nada pago não
     * repete o total — "resta R$ 21.330,00" logo abaixo de R$ 21.330,00 não informa
     * nada.
     */
    const renderPaymentInfo = (payment: {
        paid: number
        remaining: number
        settled: boolean
        overdue: boolean
    }) => {
        if (payment.settled) {
            return <span className="font-semibold text-success">Pago</span>
        }
        if (payment.paid <= 0) {
            return (
                <span
                    className={`font-semibold ${payment.overdue ? 'text-error' : 'text-base-content/60'}`}
                >
                    {payment.overdue ? 'Nada pago · vencido' : 'Nada pago'}
                </span>
            )
        }
        return (
            <>
                <span className="font-semibold text-success">
                    {currency(payment.paid)}
                </span>
                <span className="text-base-content/50">
                    {' '}
                    pago · resta {currency(payment.remaining)}
                </span>
            </>
        )
    }

    if (loading && items.length === 0) {
        return (
            <div className="flex min-h-[400px] flex-col items-center justify-center gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-sm text-base-content/60">
                    Carregando {ordersLabel}...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-5">
            <ListPageHeader
                icon={<MdPersonPin className="text-2xl" />}
                title={OrdersLabel}
                subtitle={`${total} ${total === 1 ? orderLabel : ordersLabel}`}
                search={{
                    value: searchInput,
                    onChange: setSearchInput,
                    placeholder: `Buscar ${ordersLabel}...`,
                }}
                actions={
                    <>
                        {/* Só ícone: é ação de apoio e o cabeçalho já está
                            cheio. Leva ao fechamento de caixa do próprio
                            operador (consultas + exames + procedimentos). */}
                        <button
                            onClick={() => navigate('/bookings/cash-closing')}
                            className="btn btn-sm btn-ghost btn-circle"
                            title="Fechamento de caixa"
                            aria-label="Fechamento de caixa"
                        >
                            <MdPointOfSale className="text-lg" />
                        </button>
                        {canCreate && (
                            <>
                                <button
                                    className="btn btn-sm btn-outline btn-secondary gap-2"
                                    onClick={() => setShowImportModal(true)}
                                >
                                    <MdFileDownload className="text-lg" />
                                    <span className="hidden sm:inline">
                                        Importar
                                    </span>
                                </button>
                                <button
                                    onClick={() =>
                                        navigate(
                                            '/atendimento/scheduling?type=PROCEDURE&status=BUDGET'
                                        )
                                    }
                                    className="btn btn-sm btn-accent btn-outline gap-2"
                                >
                                    <MdAdd className="text-lg" />
                                    <span className="hidden sm:inline">
                                        Orçamento
                                    </span>
                                </button>
                                <button
                                    onClick={() =>
                                        navigate(
                                            '/atendimento/scheduling?type=PROCEDURE'
                                        )
                                    }
                                    className="btn btn-sm btn-secondary gap-2"
                                >
                                    <MdAdd className="text-lg" />
                                    <span className="hidden sm:inline">
                                        {OrderLabel}
                                    </span>
                                </button>
                            </>
                        )}
                    </>
                }
            />

            <ListFilterBar activeCount={activeFilters} onClear={clearFilters}>
                <FilterField label="Número" htmlFor="order-id-filter">
                    <input
                        id="order-id-filter"
                        type="number"
                        placeholder="0"
                        value={filterById}
                        onChange={e => setFilterById(e.target.value)}
                        className="input input-bordered input-sm w-24"
                        min={1}
                    />
                </FilterField>
                <FilterField label={ClientLabel} className="w-56">
                    <ClientSelector
                        value={clientFilter}
                        onChange={id => setClientFilter(id)}
                        label=""
                        placeholder="Buscar por nome"
                        size="sm"
                    />
                </FilterField>
                <FilterField label="Status" htmlFor="order-status-filter">
                    <select
                        id="order-status-filter"
                        value={statusFilter}
                        onChange={e =>
                            setStatusFilter(e.target.value)
                        }
                        className="select select-bordered select-sm w-40"
                    >
                        <option value="">Todos</option>
                        {PROCEDURE_STATUS_FILTER_OPTIONS.map(option => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </FilterField>
                {!hidePartnerSelector && (
                    <PartnerSelector
                        value={partnerFilter}
                        onChange={id => setPartnerFilter(id)}
                        label="Parceiro"
                        placeholder="Todos"
                        className="!w-48"
                        size="sm"
                    />
                )}
                {!hidePartnerSelector && (
                    <UserSelector
                        value={sellerFilter}
                        onChange={id => setSellerFilter(id)}
                        label="Vendedor"
                        placeholder="Todos"
                        className="!w-48"
                        size="sm"
                    />
                )}
                <BillingStatusFilter
                    value={billingStatusFilter}
                    onChange={setBillingStatusFilter}
                />
                <DateRangeFilter onChange={setDateRange} />
                <ListSortSelect
                    value={sortBy}
                    onChange={setSortBy}
                    order={sortOrder}
                    onOrderChange={setSortOrder}
                />
            </ListFilterBar>

            {items.length === 0 && !loading ? (
                <ListEmptyState
                    icon={<MdPersonPin />}
                    title={`Nenhum ${orderLabel} encontrado`}
                    description={`Registre ${ordersLabel} realizados em ${clientLabel}s.`}
                    filtered={activeFilters > 0}
                    onClearFilters={clearFilters}
                    action={
                        canCreate && (
                            <button
                                onClick={() =>
                                    navigate(
                                        '/atendimento/scheduling?type=PROCEDURE'
                                    )
                                }
                                className="btn btn-secondary gap-2"
                            >
                                <MdAdd className="text-lg" />
                                Novo {orderLabel}
                            </button>
                        )
                    }
                />
            ) : (
                <>
                    <div
                        className={`grid grid-cols-1 gap-4 transition-opacity md:grid-cols-2 xl:grid-cols-3 ${
                            loading ? 'pointer-events-none opacity-50' : ''
                        }`}
                    >
                        {items.map(order => {
                            const totalPrice =
                                (order as any).totalPrice != null
                                    ? Number((order as any).totalPrice)
                                    : 0
                            const sellingPrice =
                                order.catalog?.sellingPrice ?? 0
                            const displayPrice =
                                totalPrice > 0 ? totalPrice : sellingPrice
                            const costPrice = order.catalog?.price ?? 0
                            const markup =
                                order.catalog?.markupPercentage ?? 0
                            // Custo só interessa quando o valor mostrado é o do
                            // catálogo; com totalPrice o card já fala do cobrado.
                            const showCost = totalPrice <= 0 && costPrice > 0

                            /**
                             * Quanto já entrou, somando a cobrança âncora e as
                             * parcelas penduradas nela — no flex são as avulsas que o
                             * próprio paciente gera pelo portal.
                             *
                             * Só aparece quando existe cobrança E há valor a cobrar:
                             * orçamento e atendimento sem cobrança não têm o que
                             * informar, e a linha vazia só engorda o card.
                             */
                            // O que se deve é o `finalPrice` (já com desconto), não o
                            // preço exibido — que cai no valor de catálogo quando o
                            // atendimento ainda não tem total próprio.
                            const owedPrice =
                                order.finalPrice ?? totalPrice ?? 0
                            const billingState = order.billing
                                ? resolveAppointmentBillingState(
                                      order.billing,
                                      owedPrice
                                  )
                                : null
                            // Flex ainda sem NENHUMA parcela gerada não tem cobrança
                            // âncora — e é justamente quando o operador precisa ver
                            // que nada entrou. Zero pago, tudo a pagar.
                            const paidSoFar = billingState?.paidSoFar ?? 0
                            const remainingToPay =
                                billingState?.remainingToPay ?? owedPrice
                            const charges = order.billing
                                ? [
                                      order.billing,
                                      ...(order.billing.adjustments ?? []),
                                  ]
                                : []
                            const payment =
                                // Basta haver cobrança (ou ser flex, que cobra em
                                // parcelas avulsas): um procedimento com duas
                                // cobranças vencidas e nada pago é exatamente o que
                                // o operador precisa enxergar da lista.
                                owedPrice > 0 &&
                                (order.flexPayment || charges.length > 0)
                                    ? {
                                          paid: paidSoFar,
                                          remaining: remainingToPay,
                                          settled:
                                              remainingToPay <= 0 &&
                                              paidSoFar > 0,
                                          overdue: charges.some(
                                              c => c.status === 'OVERDUE'
                                          ),
                                      }
                                    : null

                            return (
                                <div
                                    key={order.id}
                                    onClick={() =>
                                        navigate(
                                            `/bookings/orders/${order.id}`
                                        )
                                    }
                                    className="group flex cursor-pointer flex-col rounded-2xl border border-base-300 bg-base-100 transition-colors hover:border-secondary/40 hover:bg-base-200/30"
                                >
                                    <div className="flex flex-col gap-3 p-5">
                                        <div className="flex items-start gap-3">
                                            <h3 className="line-clamp-2 min-w-0 flex-1 text-base font-semibold text-base-content">
                                                {/* Venda de loja não tem item âncora
                                                    (`catalog`): quem nomeia é o
                                                    `title`, montado dos itens do
                                                    carrinho. */}
                                                {order.catalog?.name ||
                                                    order.title ||
                                                    OrderLabel}
                                            </h3>
                                            {/* Sem text-white forçado: o badge do
                                                tema já traz o par de cores certo. */}
                                            <span
                                                className={`badge badge-sm shrink-0 ${getOrderStatusColor(
                                                    order.status,
                                                    order.scheduledAt
                                                )}`}
                                            >
                                                {getOrderStatusLabel(
                                                    order.status,
                                                    order.scheduledAt
                                                )}
                                            </span>
                                        </div>

                                        <div className="flex flex-col gap-1.5">
                                            {order.client && (
                                                <div className="flex items-center gap-2 text-sm text-base-content/70">
                                                    <MdPerson className="shrink-0 text-base text-base-content/40" />
                                                    <span className="truncate">
                                                        {order.client.name}
                                                    </span>
                                                </div>
                                            )}
                                            {order.partner && (
                                                <div className="flex items-center gap-2 text-sm text-base-content/60">
                                                    <MdBusiness className="shrink-0 text-base text-base-content/40" />
                                                    <span className="truncate">
                                                        {order.partner
                                                            .name ||
                                                            order.partner
                                                                .tradingName}
                                                    </span>
                                                </div>
                                            )}
                                            {order.healthPlan && (
                                                <div className="flex items-center gap-2 text-sm text-base-content/60">
                                                    <MdHealthAndSafety className="shrink-0 text-base text-base-content/40" />
                                                    <span className="truncate">
                                                        {
                                                            order.healthPlan
                                                                .name
                                                        }
                                                    </span>
                                                </div>
                                            )}
                                        </div>

                                        {order.description && (
                                            <p className="line-clamp-2 text-sm leading-relaxed text-base-content/50">
                                                {order.description}
                                            </p>
                                        )}

                                        {/* Custo e markup viram etiquetas em vez de
                                            uma linha de texto solta: continuam
                                            visíveis, sem competir com o valor de
                                            venda no rodapé. */}
                                        {(order.packageCatalogId != null ||
                                            order.origin === 'STORE' ||
                                            order.origin === 'COUNTER' ||
                                            order.catalog?.catalogType ||
                                            order.room ||
                                            showCost ||
                                            markup > 0) && (
                                            <div className="flex flex-wrap gap-1.5">
                                                {order.packageCatalogId !=
                                                    null && (
                                                    <span className="badge badge-sm badge-success badge-outline">
                                                        Pacote
                                                    </span>
                                                )}
                                                {/* Venda da vitrine: tem entrega e
                                                    cupom, e não tem quem comparecer.
                                                    Sem a etiqueta, o operador a
                                                    confunde com atendimento. */}
                                                {order.origin === 'STORE' && (
                                                    <span className="badge badge-sm badge-info badge-outline">
                                                        Loja
                                                    </span>
                                                )}
                                                {/* Venda de balcão: mercadoria entregue na
                                                    hora, sem executante e sem quem
                                                    comparecer. Mesma razão da etiqueta da
                                                    loja — sem ela, some no meio dos
                                                    atendimentos. */}
                                                {order.origin === 'COUNTER' && (
                                                    <span className="badge badge-sm badge-primary badge-outline">
                                                        Balcão
                                                    </span>
                                                )}
                                                {order.catalog
                                                    ?.catalogType && (
                                                    <span className="badge badge-sm badge-ghost">
                                                        {
                                                            order.catalog
                                                                .catalogType
                                                                .name
                                                        }
                                                    </span>
                                                )}
                                                {showCost && (
                                                    <span className="badge badge-sm badge-ghost">
                                                        Custo{' '}
                                                        {currency(costPrice)}
                                                    </span>
                                                )}
                                                {markup > 0 && (
                                                    <span className="badge badge-sm badge-ghost">
                                                        Markup{' '}
                                                        {markup.toFixed(1)}%
                                                    </span>
                                                )}
                                                {order.room && (
                                                    <span className="badge badge-sm badge-ghost">
                                                        {order.room}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    <div className="mt-auto flex items-center justify-between gap-3 border-t border-base-300 px-5 py-3">
                                        <span className="flex min-w-0 items-center gap-1.5 text-xs text-base-content/50">
                                            <MdAccessTime className="shrink-0 text-sm" />
                                            <span className="truncate">
                                                {order.scheduledAt
                                                    ? format(
                                                          new Date(
                                                              order.scheduledAt
                                                          ),
                                                          "dd/MM/yyyy 'às' HH:mm",
                                                          { locale: ptBR }
                                                      )
                                                    : format(
                                                          new Date(
                                                              order.createdAt
                                                          ),
                                                          'dd/MM/yyyy',
                                                          { locale: ptBR }
                                                      )}
                                            </span>
                                        </span>
                                        <span className="flex shrink-0 items-center gap-2">
                                            {displayPrice > 0 && (
                                                <span className="text-sm font-bold text-secondary">
                                                    {currency(displayPrice)}
                                                </span>
                                            )}
                                            <MdArrowForward className="text-base text-base-content/30 transition-colors group-hover:text-secondary" />
                                        </span>
                                    </div>

                                    {/* Quanto já entrou. No FLEX o valor do card é o
                                        combinado, e o paciente paga em parcelas que ele
                                        mesmo gera — sem esta linha, a lista mostra
                                        R$ 21.900 num procedimento com R$ 3.500 pagos. */}
                                    {payment && (
                                        <div className="flex items-center justify-between gap-3 border-t border-base-300 px-5 py-2">
                                            {order.flexPayment ? (
                                                <>
                                                    <span className="badge badge-sm badge-accent badge-outline shrink-0">
                                                        FLEX
                                                    </span>
                                                    <span className="min-w-0 truncate text-right text-xs">
                                                        {renderPaymentInfo(
                                                            payment
                                                        )}
                                                    </span>
                                                </>
                                            ) : (
                                                <span className="min-w-0 truncate text-xs">
                                                    {renderPaymentInfo(payment)}
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )
                        })}
                    </div>

                    {total > limit && (
                        <PaginationControls
                            page={page}
                            limit={limit}
                            total={total}
                            loading={loading}
                            onPageChange={setPage}
                            label={orderLabel}
                        />
                    )}
                </>
            )}
            <ImportDataModal
                isOpen={showImportModal}
                onClose={() => setShowImportModal(false)}
                module={ShareableModule.PROCEDURE}
                moduleLabel={OrdersLabel}
                hideFileImport
                fetchItems={async (companyId: number) => {
                    const response = await getOrdersByCompany(companyId, {
                        packages: 'exclude',
                    })
                    return response.success ? response.result : []
                }}
                renderItem={(item: Order) => (
                    <div>
                        <p className="truncate text-sm font-medium">
                            {item.catalog?.name || OrderLabel}
                        </p>
                        <p className="truncate text-xs text-base-content/60">
                            {item.client?.name} -{' '}
                            {getOrderStatusLabel(
                                item.status,
                                item.scheduledAt
                            )}
                        </p>
                    </div>
                )}
                getItemId={(item: Order) => item.id}
                getItemName={(item: Order) =>
                    item.catalog?.name || OrderLabel
                }
                onImportComplete={reload}
            />
        </div>
    )
}
